//================================================================
//  Component: View Commercial Task Pane
//================================================================

//  Purpose: This side pane allows business partners to view a commercial task

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the side pane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the side pane}

//  Example:
//  
//    <ViewCommercialTaskPane
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//    ></ViewCommercialTaskPane>

//================================================================

//Libraries
import React, { useState, useReducer, useEffect, useContext } from 'react';
import { GetUser } from '../../../../../Library/GlobalContexts';

//Contexts

//Components
import QueryListener from '../../../../../Library/QueryListener';
import QuoteDetail from '../Components/QuoteDetail';

//Functions
import WriteDocument from '../../../../../Library/WriteDocument';

//Images
import Loading from '../../../../../Components/Images/Image_Loading_Ripple.svg';
import FileIcon from '../../../../../Components/Images/Icon_File_Black.svg'
import GoBack from '../../../../../Components/Images/Icon_GoBack_Grey.svg';
import InfoRequired from '../../../../../Components/Images/Icon_InfoRequired_Blue.svg';
import TabViewSidePane from '../../../../../Components/TabViewSidePane/TabViewSidePane';
import HistoryTimeline from '../../../../../Components/HistoryTimeline/HistoryTimeline';

//CSS


export default function ViewCommercialTaskPane({
  paneOpen,
  setPaneOpen
}) {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useState
  //------------------------------------------------------

  // Used to set pane status > 'onload', 'pending', 'error'
  const [paneStatus, setPaneStatus] = useState('onload');

  // Used to store quotes
  const [quoteApprovals, setQuoteApprovals] = useState([]);

  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store the quote pane state/state
  const [quotePaneData, setQuotePaneData] = useReducer(
      (state, newState) => ({...state, ...newState}),
      {
          'saveDisabled': true,       // used to determine if the save button is disabled
          'rejectionReason': [],      // used to save the rejection reason
          'status': '',               // used to save the status/action for the quote
          'currentQuote': undefined   // used to store the current quote
      }
  );

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Save Changes
  function saveChanges() {

    setPaneStatus('pending');

    // ---------------------------------------------------
    //  Update Quote Review Task
    // ---------------------------------------------------

    const updatedDoc = {
      'status': quotePaneData?.status,
      'rejectionreason': quotePaneData?.status === 'Rejected' ? quotePaneData?.rejectionReason : '',
      'lastmodifieddate': new Date(),
      'lastmodifiedby': {
          'email': getUser?.emailaddress,
          'givenname': getUser?.givenname,
          'surname': getUser?.surname
      },
      'activityfeed': [
        ...quotePaneData?.currentQuote?.activityfeed,
        {
          'activitydate': new Date(),
          'actionedby': {
            'email': getUser?.emailaddress,
            'givenname': getUser?.givenname,
            'surname': getUser?.surname
          },
          'action': quotePaneData?.status === 'Rejected' ? 'rejected' : 'approved',
          'comments': quotePaneData?.status === 'Rejected' ? quotePaneData?.rejectionReason : ''
        }
      ]
    }

    return WriteDocument('requests', quotePaneData?.currentQuote?.requestid, updatedDoc, true)
    .then(() => {

      // Reset state and close the side pane
      setQuotePaneData({
          'saveDisabled': true,
          'rejectionReason': [],
          'status': '',
          'currentQuote': undefined
      });
      setPaneStatus('onload');
      setPaneOpen({ 'viewCommercial': undefined });

    })
    .catch((error) => {

      console.log('error', error);
      setPaneStatus('error');

    })

  }

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get the quote review requests
  useEffect(() => {

    if (paneOpen === undefined) return;
    if (paneOpen?.viewCommercial?.parentrequestid === undefined) return;

    function onLoadChange(documents) {

      // Sort Newest to Oldest
      documents = documents.sort((a, b) => {
        return b.createddate - a.createddate;
      });

      setQuoteApprovals(documents);

    }

    function onError(error) {

      console.log('error', error);
      setPaneStatus('error');

    }

    const unsubscribe = QueryListener('requests', [
      ['tasktype', '==', 'Quote Review'],
      ['status', 'not-in', ['Draft', 'Initialised']],
      ['poanumber', '==', paneOpen?.viewCommercial?.poanumber],
    ], onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [paneOpen])

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  // ==================================================
  //  Hide Side Pane
  // ==================================================

  if (paneOpen?.viewCommercial === undefined) return null;

  // ==================================================
  //  Show Side Pane  
  // ==================================================
  // ---------------------------------------------------
  //  Onload
  // ---------------------------------------------------

  if (paneStatus === 'onload') {
    return (
      <div className='Pane-Background '>
        <dialog className='Pane-Container' style={{ width: '70vw' }}>
          <div className='flex flex-col h-full overflow-x-hidden w-full'>

            {/* Body */}
            <div className='h-[calc(100%-78px)] flex flex-col border border-solid gap-3'>

              <TabViewSidePane
                defaultView={1}
                oneTitle={'Demand Commercial Request'}
                oneContent={
                  <form className='flex items-center justify-between w-full px-2 overflow-x-auto' >

                    {/* Body */}
                    <div className='w-[50vw] h-full flex flex-col overflow-y-auto'>
                      <div className='Form'>

                        {/* ------------- REQUEST INFORMATION ------------- */}

                        <h5 className='text-[18px] mt-2 mb-1'>Request Information</h5>

                        {/* =========================================================== */}
                        {/*  1. Request Type                                            */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 1. Request Type <span className='text-[#C4314B]'>*</span></label>
                          <select disabled className='Input-Field-Select' style={{ width: '300px' }}>
                            <option>{paneOpen?.viewCommercial?.requesttype}</option>
                          </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  2. Application Name                                        */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 2. Application Name <span className='text-[#C4314B]'>*</span></label>
                          <input
                            disabled
                            className='Input-Field-Text'
                            value={paneOpen?.viewCommercial?.applicationname}
                            style={{ width: '300px' }}
                          ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  3. Short Description                                       */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 3. Short Description <span className='text-[#C4314B]'>*</span></label>
                          <textarea
                            disabled
                            className='Input-Field-TextArea'
                            value={paneOpen?.viewCommercial?.shortdescription}
                          ></textarea>
                        </div>

                        {/* =========================================================== */}
                        {/*  4. Requester Name                                          */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 4. Requester Name <span className='text-[#C4314B]'>*</span></label>
                          <input
                            disabled
                            className='Input-Field-Text'
                            value={paneOpen?.viewCommercial?.requestername}
                            style={{ width: '400px' }}
                          ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  5. Region                                                  */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 5. Region <span className='text-[#C4314B]'>*</span></label>
                          <select disabled className='Input-Field-Select' style={{ width: '300px' }}>
                            <option>{paneOpen?.viewCommercial?.region}</option>
                          </select>
                        </div>

                        {/* =========================================================== */}
                        {/* 6. Sponsor Name                                             */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 6. Sponsor Name <span className='text-[#C4314B]'>*</span></label>
                          <input
                            disabled
                            className='Input-Field-Text'
                            value={paneOpen?.viewCommercial?.sponsorname?.email}
                            style={{ width: '400px' }}
                          ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  7. Supporting Documents                                    */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 7. Supporting Documents </label>

                          {/* Uploads */}
                          {
                            paneOpen?.viewCommercial?.supportingdocs?.length > 0 ?

                              <div className='w-full flex flex-col rounded-md border border-[#D8D8D8]'>
                                {
                                  paneOpen?.viewCommercial?.supportingdocs?.map((file, index) => (
                                    <div key={index} className='flex flex-row justify-between items-center border-b border-b-[#D8D8D8] last:border-0 px-3 py-1'>

                                      {/* File Name */}
                                      <div className='flex flex-row gap-2 items-center'>
                                        <img src={FileIcon} alt='File'></img>
                                        <a href={file.fileUrl} target='_blank' rel='noreferrer'>{file.fileName}</a>
                                      </div>
                                    </div>
                                  ))
                                }

                              </div>
                              :
                              <span className='text-gray-500'>No documents</span>
                          }
                        </div>

                        {/* ------------- CONTRACT INFORMATION ------------- */}

                        <h5 className='text-[18px] mt-4 mb-2'>Contract Information</h5>

                        {/* =========================================================== */}
                        {/*  8. Detailed Request / Scope Description                    */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 8. Detailed Request / Scope Description <span className='text-[#C4314B]'>*</span></label>
                          <textarea
                            disabled
                            className='Input-Field-TextArea'
                            value={paneOpen?.viewCommercial?.commercialscope}
                          ></textarea>
                        </div>

                        {/* =========================================================== */}
                        {/*  9. Buy / Sell Agreement                                    */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 9. Is this a Buy or Sell Agreement? <span className='text-[#C4314B]'>*</span></label>
                          <select disabled className='Input-Field-Select' style={{ width: '300px' }}>
                            <option>{paneOpen?.viewCommercial?.buysellagreement}</option>
                          </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  10. Agreement Type                                         */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 10. Agreement Type <span className='text-[#C4314B]'>*</span></label>
                          <select disabled className='Input-Field-Select' style={{ width: '300px' }}>
                            <option>{paneOpen?.viewCommercial?.agreementtype}</option>
                          </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  11. Lendlease Company                                      */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 11. Lendlease Company (Signing Entity) <span className='text-[#C4314B]'>*</span></label>
                          <select className='Input-Field-Select' disabled>
                            <option>{paneOpen?.viewCommercial?.lendleasecompany}</option>
                          </select>
                        </div>

                        {/* ------------- OTHER PARTY INFORMATION (VENDOR/CUSTOMER) ------------- */}

                        <h5 className='text-[18px] mt-4 mb-2'> Other Party Information (Vendor / Customer) </h5>

                        {/* =========================================================== */}
                        {/*  12. Vendor Name                                            */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 12. Vendor Name <span className='text-[#C4314B]'>*</span></label>

                          {
                            paneOpen?.viewCommercial?.newvendorname === '' ?
                              <input
                                disabled
                                className='Input-Field-Text'
                                value={paneOpen?.viewCommercial?.vendor?.vendorname}
                                style={{ width: '400px' }}
                              ></input>
                              :
                              <div className='flex flex-col justify-center bg-[#E7F0F4] px-4 py-3 rounded-md'>

                                {/* Header */}
                                <div className='flex flex-row gap-2 items-center'>
                                  <img src={InfoRequired} alt='info'></img>
                                  <label className='font-medium text-[#5E93AC]'>New Vendor Request</label>
                                </div>

                                {/* Form */}
                                <div className='grid grid-cols-2 gap-2'>

                                  {/* Column 1 */}
                                  <div className='flex flex-col gap-2 px-2 mt-0 mb-2'>

                                    {/* Vendor Name */}
                                    <div className='Task-Form-Row'>
                                      <label className='font-medium'> Vendor Name <span className='text-[#C4314B]'>*</span> </label>
                                      <input
                                        disabled
                                        className='Input-Field-Text'
                                        value={paneOpen?.viewCommercial?.newvendorname}
                                        style={{ width: '100%' }}
                                      ></input>
                                    </div>

                                    {/* Vendor Phone */}
                                    <div className='Task-Form-Row'>
                                      <label className='font-medium'> Vendor Phone </label>
                                      <input
                                        disabled
                                        className='Input-Field-Text'
                                        value={paneOpen?.viewCommercial?.newvendorcontactphone}
                                        style={{ width: '100%' }}
                                      ></input>
                                    </div>

                                  </div>

                                  {/* Column 2 */}
                                  <div className='flex flex-col gap-2 px-2 mt-0 mb-2'>

                                    {/* Vendor Trading As Name */}
                                    <div className='Task-Form-Row'>
                                      <label className='font-medium'> Vendor Trading As Name <span className='text-[#C4314B]'>*</span> </label>
                                      <input
                                        disabled
                                        className='Input-Field-Text'
                                        value={paneOpen?.viewCommercial?.newvendortradingname}
                                        style={{ width: '100%' }}
                                      ></input>
                                    </div>

                                    {/* Vendor Email */}
                                    <div className='Task-Form-Row'>
                                      <label className='font-medium'> Vendor Email </label>
                                      <input
                                        disabled
                                        className='Input-Field-Text'
                                        value={paneOpen?.viewCommercial?.newvendorcontactemail}
                                        style={{ width: '100%' }}
                                      ></input>
                                    </div>

                                  </div>
                                </div>

                                {/* Return Button */}
                                <div className='flex justify-end my-2 mx-4'>
                                  <button className='Primary-Button flex flex-row gap-2' disabled>
                                    <img src={GoBack} alt='return'></img>
                                    Choose Existing Vendor
                                  </button>
                                </div>

                              </div>
                          }

                        </div>

                        {/* ------------- FINANCIAL INFORMATION ------------- */}

                        <h5 className='text-[18px] mt-4 mb-2'> Financial Information </h5>

                        {/* =========================================================== */}
                        {/*  13. Currency                                               */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 13. Currency </label>
                          <select disabled className='Input-Field-Select' style={{ width: '300px' }}>
                            <option>{paneOpen?.viewCommercial?.currency?.length === 0 ? '-' : paneOpen?.viewCommercial?.currency}</option>
                          </select>
                        </div>

                        {/* =========================================================== */}
                        {/*  14. Estimated Annual Contract Value (exGST)                 */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 14. Estimated Annual Contract Value (exGST)</label>
                          <input
                            disabled
                            className='Input-Field-Text'
                            value={paneOpen?.viewCommercial?.estannualcontractvalue?.length === 0 ? '-' : paneOpen?.viewCommercial?.estannualcontractvalue}
                            style={{ width: '400px' }}
                          ></input>
                        </div>

                        {/* =========================================================== */}
                        {/*  15. Estimated Total Contract Value (exGST)                  */}
                        {/* =========================================================== */}

                        <div className='Form-Row'>
                          <label className='font-medium'> 15. Estimated Total Contract Value (exGST)</label>
                          <input
                            disabled
                            className='Input-Field-Text'
                            value={paneOpen?.viewCommercial?.esttotalcontractvalue?.length === 0 ? '-' : paneOpen?.viewCommercial?.esttotalcontractvalue}
                            style={{ width: '400px' }}
                          ></input>
                        </div>

                        {/* ============================ */}
                        {/*  16. Recharge Information *  */}
                        {/* ============================ */}

                        <div className='Form-Row'>
                          <label><b style={{ fontWeight: '500' }}> 16. Recharge Information </b> </label>

                          <i className='mb-3'>Project code, task number and expenditure type allow the recharge of technology costs.</i>

                          {/* Project Code */}
                          <div className='flex flex-row'>
                            <label className='w-[130px]'>Project Code</label>
                            <input
                              className='Input-Field-Text'
                              type='text'
                              placeholder={'Project Code'}
                              value={paneOpen?.viewCommercial?.projectcode}
                              disabled={true}
                            ></input>
                          </div>

                          {/* Task Number */}
                          <div className='flex flex-row'>
                            <label className='w-[130px]'>Task Number</label>
                            <input
                              className='Input-Field-Text'
                              type='text'
                              placeholder={'Task Number'}
                              value={paneOpen?.viewCommercial?.tasknumber}
                              disabled={true}
                            ></input>
                          </div>

                          {/* Expenditure Type */}
                          <div className='flex flex-row'>
                            <label className='w-[130px]'>Expenditure Type</label>
                            <input
                              className='Input-Field-Text'
                              type='text'
                              placeholder={'Expenditure Type'}
                              value={paneOpen?.viewCommercial?.expendituretype}
                              disabled={true}
                            ></input>
                          </div>
                        </div>

                      </div>

                    </div>

                  </form>
                }
                twoTitle={'Quotes'}
                twoCount={quoteApprovals.filter((item) => item?.status === 'For Review')?.length}
                twoContent={
                  <div className='w-[57vw] rounded-lg'>
                    {
                      // Check if quotes exist
                      quoteApprovals?.length === 0 || quoteApprovals === undefined ?

                      // No Quotes
                        <div className='h-[400px] flex items-center justify-center'>
                          Hang tight—your Commercial Manager has not shared any quotes with you yet.
                        </div>
                      :
                      // Show Quotes
                      <div className='h-full flex flex-col gap-3'>
                        {
                          quoteApprovals?.map((item, index) => (
                            <QuoteDetail
                              key={index}
                              index={index}
                              quote={item}
                              quotePaneData={quotePaneData}
                              setQuotePaneData={setQuotePaneData}
                            ></QuoteDetail>
                          ))
                        }
                      </div>
                    }
                  </div>
                }
                threeTitle={'History'}
                threeContent={
                  <>
                    <div>
                      <div className='w-full max-w-3xl ml-[-18px]'>

                        {
                          paneOpen?.viewCommercial?.activityfeed?.toReversed().map((item, index) => (
                            <HistoryTimeline
                              item={item}
                              key={index}
                            ></HistoryTimeline>
                          ))
                        }

                      </div>
                    </div>
                  </>
                }
              >

              </TabViewSidePane>
            </div>

            {/* Footer */}
            <div className='px-4 py-3'>
              <div className='flex flex-row gap-2'>

                {/* Save */}
                <button
                  className={`Primary-Button ${quotePaneData.saveDisabled ? 'Primary-Button:disabled' : ''}`}
                  disabled={quotePaneData.saveDisabled}
                  onClick={saveChanges}
                >
                  Save
                </button>

                {/* Cancel */}
                <button
                  className='Secondary-Button'
                  onClick={() => {

                    // Reset Form and Close Side Pane
                    setQuotePaneData({
                      'saveDisabled': '',
                      'rejectionReason': ''
                    });
                    setPaneOpen({ 'viewCommercial': undefined });

                  }}
                >
                  Cancel
                </button>

              </div>
            </div>

          </div>
        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Pending
  // ---------------------------------------------------

  else if (paneStatus === 'pending') {
    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container flex justify-center items-center text-center'>
          <img alt='loading-circle-icon' src={Loading}></img>
        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Error
  // ---------------------------------------------------

  else if (paneStatus === 'error') {
    return (
      <div className='Pane-Background'>

        {/* Pane Container */}
        <dialog className='Pane-Container'>
          <div className='flex flex-col gap-2 w-full justify-center items-center'>

            <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
            <h4 className='mb-0'>Oops! Something went wrong.</h4>
            <p className='text-center leading-[1.7]'>
              An error occurred while we processed your request.
            </p>
            <button
              className='Primary-Button'
              onClick={() => {

                // Reset Form and Close Pane
                setPaneOpen({ 'viewCommercial': undefined });

              }}
            >
              Close
            </button>

          </div>
        </dialog>
      </div>

    )
  }

  //------------------------------------------------------

}